import type { PlanRoom } from "@sts/geometry";
import type { Jurisdiction } from "@sts/rules-data";
import type { PlanObjectOnPlan } from "./plan-geometry.js";

/**
 * The shapes of the MCS design check on the floor plan (`design-check.ts`).
 *
 * One vocabulary for the portal (which draws it live as the designer moves
 * things), the API (`GET /api/surveys/:id/design-check`) and, later, the
 * installer pack — so all three state the same verdicts in the same words.
 */

/**
 * The four states every line of the check is in, each explained in words
 * wherever it is shown:
 *
 *   pass    — meets the rule it names (green).
 *   fail    — does not; the message says by how much (red).
 *   warn    — passes on what the plan shows, but something must be confirmed
 *             on site or rests on an assumption the message names (amber).
 *   unknown — cannot be assessed yet; the message says what is missing (grey).
 *             Never shown as a pass.
 */
export type CheckState = "pass" | "fail" | "warn" | "unknown";

/** A survey room with its heat loss, as the engine calculated it. */
export interface DesignCheckHeatLossRoom {
  /** The survey room's id. */
  readonly roomId: string;
  /** The scanned room it came from (`capturedRoomRef`), or null for a room entered by hand. */
  readonly externalRef: string | null;
  readonly name: string;
  readonly designTempC: number;
  /** `RoomHeatLoss.totalW` — transmission + bridging + ventilation, W. */
  readonly heatLossW: number;
  readonly floorAreaM2: number;
}

export type DesignCheckHeatLoss =
  | {
      readonly available: true;
      /** `DwellingHeatLoss.totalHeatLossW`: the design heat load, W. */
      readonly totalHeatLossW: number;
      readonly rooms: readonly DesignCheckHeatLossRoom[];
    }
  | {
      readonly available: false;
      /** Why there is none, in words a designer can act on. */
      readonly reason: string;
    };

/** Everything the check needs that is not on the drawing. */
export interface DesignCheckContext {
  readonly calculationDate: string;
  readonly jurisdiction: Jurisdiction;
  /** The survey's design external temperature (MIS 3005-D Table 2), °C; null with no survey conditions. */
  readonly externalDesignTempC: number | null;
  readonly heatLoss: DesignCheckHeatLoss;
  /** The designer's design flow temperature, °C (design decisions); null until set. */
  readonly designFlowTempC: number | null;
  /** The designer's flow-minus-return, K; null until set (the check then assumes the design pack's 5 K and says so). */
  readonly systemDeltaTK: number | null;
  /** From the design decisions; absent means none recorded (the design decisions' own default is "ashp"). */
  readonly systemType?: "ashp" | "gshp" | "hybrid" | "air-to-air";
}

export interface DesignCheckInput {
  readonly context: DesignCheckContext;
  /** Every storey's scanned rooms, from `scanPlanRooms`. */
  readonly rooms: readonly PlanRoom[];
  /** Every storey's plan objects. */
  readonly objects: readonly PlanObjectOnPlan[];
}

/** One line of the compact summary: what, how it stands, and what to select to see why. */
export interface DesignCheckLine {
  readonly key: "heat-loss" | "heat-pump-needed" | "heat-pump-chosen" | "radiators" | "noise" | "placement";
  readonly state: CheckState;
  readonly text: string;
  /** The plan object the line is about, when it is about one. */
  readonly objectId?: string;
  /** The scanned room (storey + index) the line is about, when it is about one. */
  readonly room?: { readonly story: number; readonly index: number };
}

// ------------------------------------------------------------ formatting ----

/** "1,610 W" */
export function formatW(w: number): string {
  return `${Math.round(w).toLocaleString("en-GB")} W`;
}

/** "7.43 kW" */
export function formatKw(w: number, dp = 2): string {
  return `${(w / 1000).toFixed(dp)} kW`;
}

/** "−3 °C" with a real minus sign. */
export function formatC(c: number): string {
  const rounded = Math.round(c * 10) / 10;
  const text = Number.isInteger(rounded) ? String(Math.abs(rounded)) : Math.abs(rounded).toFixed(1);
  return `${rounded < 0 ? "−" : ""}${text} °C`;
}

/**
 * A percentage for a pass/fail line, rounded DOWN to 0.1 so a figure that
 * fails is never printed as "100%": 99.96% reads "99.9%".
 */
export function formatPercent(fraction: number, opts: { readonly failing?: boolean } = {}): string {
  // The 1e-6 absorbs floating-point dust (0.999 arriving as 0.99899999973);
  // `failing` then caps a result that is short at 99.9%, whatever the dust.
  let tenths = Math.floor(fraction * 1000 + 1e-6) / 10;
  if (opts.failing && tenths >= 100) tenths = 99.9;
  return `${tenths.toFixed(tenths >= 10 && Number.isInteger(tenths) ? 0 : 1)}%`;
}
